'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { Article } from '@/types/article';
import { useDarkMode } from '@/lib/DarkModeContext';
import { getArticleUrl } from '@/lib/slugs';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

interface HeroSliderProps {
    articles: Article[];
}

export default function HeroSlider({ articles }: HeroSliderProps) {
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();
    const [currentSlide, setCurrentSlide] = useState(0);
    const latestArticles = articles.slice(0, 3);

    // Reset slide index when articles list changes (e.g. language switch)
    useEffect(() => {
        setCurrentSlide(0);
    }, [articles]);

    // Touch / swipe support for mobile
    const touchStartX = useRef<number | null>(null);
    const touchStartTime = useRef<number>(0);
    const didSwipe = useRef(false);

    const onTouchStart = (e: React.TouchEvent) => {
        const x = e.touches?.[0]?.clientX;
        touchStartX.current = x ?? null;
        touchStartTime.current = Date.now();
        didSwipe.current = false;
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const deltaX = e.touches[0].clientX - touchStartX.current;
        if (Math.abs(deltaX) > 15) didSwipe.current = true;
    };

    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const endX = e.changedTouches[0].clientX;
        const delta = endX - touchStartX.current;
        const threshold = 50; // minimumpx to consider a swipe
        if (Math.abs(delta) >= threshold) {
            if (delta > 0) prevSlide();
            else nextSlide();
        }
        touchStartX.current = null;
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLAnchorElement> | React.TouchEvent<HTMLAnchorElement>) => {
        // Prevent accidental navigation after a swipe gesture
        if (didSwipe.current) {
            e.preventDefault();
            didSwipe.current = false;
        }
    };

    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % latestArticles.length);
    }, [latestArticles.length]);

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + latestArticles.length) % latestArticles.length);
    };

    // Auto-advance slides every 5 seconds
    useEffect(() => {
        if (latestArticles.length === 0) return;

        const interval = setInterval(nextSlide, 5000);
        return () => clearInterval(interval);
    }, [latestArticles.length, nextSlide]);

    if (latestArticles.length === 0) {
        return null;
    }

    // Defensive: avoid undefined if currentSlide is out of bounds
    const currentArticle = latestArticles[currentSlide] || null;

    // Category name translation map (SR → EN)
    const categoryTranslations: Record<string, string> = {
        'Vijesti': 'News',
        'Naoružanje': 'Armament', 'Municija': 'Ammunition',
        'Namjenska preduzeća': 'Defense Enterprises', 'Vojna istorija': 'Military History',
        'Kopnene snage': 'Ground Forces', 'Vazduhoplovstvo i PVO': 'Air Force & Air Defense',
        'Mornarica': 'Navy', 'Strateške snage': 'Strategic Forces',
        'Naoružanje Trećeg rajha': 'Third Reich Armaments', 'Naoružanje bivše JNA': 'Former JNA Armaments',
        'Savremeno naoružanje': 'Modern Armaments', 'Konstrukcija naoružanja': 'Weapon Construction',
        'Tenkovi i oklopna vozila': 'Tanks & Armored Vehicles', 'Artiljerija': 'Artillery',
        'Samostreli': 'Crossbows', 'Pištolji': 'Pistols', 'Revolveri': 'Revolvers',
        'Automati': 'Submachine Guns', 'Puške': 'Rifles', 'Karabini': 'Carbines',
        'Puškomitraljezi': 'Light Machine Guns', 'Mitraljezi': 'Machine Guns',
        'Ručni bacači': 'Hand Launchers', 'Ručni raketni bacači': 'Rocket Launchers',
        'Bacači granata': 'Grenade Launchers', 'Trombloni': 'Rifle Grenade Launchers',
        'Avioni i helikopteri': 'Aircraft & Helicopters', 'PVO sistemi i raketno naoružanje': 'Air Defense & Missile Systems',
        'Bespilotne letjelice': 'Unmanned Aerial Vehicles (UAV)',
        'Površinska plovila': 'Surface Vessels', 'Podmornica': 'Submarines', 'Mornaričko naoružanje': 'Naval Weapons',
        'Pješadijska municija': 'Infantry Ammunition', 'Minobacačka municija': 'Mortar Ammunition',
        'Klasična artiljerijska municija': 'Classic Artillery Ammunition',
        'Raketna artiljerijska municija': 'Rocket Artillery Ammunition',
        'Vodjene rakete': 'Guided Missiles', 'Ručne bombe': 'Hand Grenades',
        'Mine za ručne bacače': 'Grenades for Handheld Launchers',
        'Municija za bestrzajne topove': 'Recoilless Cannon Ammunition',
        'Balistika': 'Ballistics', 'Savremena municija': 'Modern Ammunition',
        'Eksplozivne materije': 'Explosives', 'Elementi municije': 'Ammunition Elements',
        'Pirotehnika': 'Pyrotechnics', 'Upaljači': 'Fuzes',
        'Istorija razvoja': 'History of Development', 'Proizvodni kapaciteti': 'Production Capacity',
        'Stari jugoslovenski projekti': 'Old Yugoslav Projects',
        'Modernizacija i izvozni program': 'Modernization & Export Program',
        'Vojske kroz vijekove': 'Armies Through the Ages',
        'I svjetski rat': 'World War I', 'II svjetski rat': 'World War II',
        'Hladni rat': 'Cold War', 'Poznati konstruktori': 'Famous Designers',
        'Nekategorisano': 'Uncategorized',
    };

    const translateCategory = (name: string) =>
        siteLang === 'en' ? (categoryTranslations[name] ?? name) : name;

    // Estimate reading time based on content length
    const getReadingTime = (article: Article): number => {
        const text = Array.isArray(article.detaljan_opis)
            ? article.detaljan_opis.filter(b => b.type === 'text').map(b => b.value).join(' ')
            : article.detaljan_opis || '';
        const wordCount = text.split(/\s+/).length;
        return Math.max(1, Math.ceil(wordCount / 200));
    };

    if (!currentArticle) {
        return null;
    }

    return (
        <section className="relative w-full mt-10 sm:mt-8 md:mt-8 lg:mt-8 px-4 lg:px-[calc(1rem+180px)] pt-6" style={{ backgroundColor: darkMode ? '#111827' : '#ffffff' }}>
            <div className="relative w-full overflow-hidden h-64 sm:h-80 md:h-[480px] lg:h-[640px] rounded-[8px] md:rounded-[8px] lg:rounded-[8px]">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0">
                    {currentArticle.naslovna_slika ? (
                        <img
                            src={currentArticle.naslovna_slika}
                            alt={currentArticle.naslov}
                            className="w-full h-full object-fill object-center md:object-cover md:object-top transition-transform"
                        />
                    ) : (
                        <div className="w-full h-full bg-gray-800" />
                    )}
                    {/* Dark gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
                </div>

                {/* Content */}
                <div className="relative h-full flex flex-col justify-end p-4 sm:p-6 lg:p-12">
                    {/* Category Badge */}
                    <span
                        className="inline-block px-3 py-1 text-white text-xs font-semibold mb-3 w-fit"
                        style={{ backgroundColor: '#b91c1c', borderRadius: '4px' }}
                    >
                        {currentArticle.kategorija === 'Vijesti'
                            ? translateCategory(currentArticle.kategorija)
                            : translateCategory(currentArticle.podpodkategorija || currentArticle.podkategorija || currentArticle.kategorija)}
                    </span>

                    {/* Title */}
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 max-w-4xl leading-tight">
                        {currentArticle.naslov}
                    </h2>

                    {/* Description */}
                    <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-4 max-w-3xl line-clamp-3">
                        {currentArticle.kratak_opis}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-6 text-gray-300 text-sm mb-6">
                        <div className="hidden sm:flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{currentArticle.autor}</span>
                        </div>

                        {/* Reading time visible on tablet+ */}
                        <div className="hidden sm:flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{getReadingTime(currentArticle)} {siteLang === 'en' ? 'min read' : 'min čitanja'}</span>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <Link
                        href={getArticleUrl(currentArticle)}
                        className="hidden sm:inline-block px-8 py-3 text-white font-semibold transition-all hover:opacity-90 w-fit"
                        style={{ backgroundColor: '#b91c1c', borderRadius: '8px' }}
                    >
                        {siteLang === 'en' ? 'Read Full Story' : 'Pročitaj Celu Priču'}
                    </Link>

                    {/* Mobile-only footer at the bottom of the hero (site name + reading time) */}
                    <div className="absolute left-0 right-0 bottom-8 z-10 sm:hidden">
                        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between text-gray-300 text-xs">
                            <span className="opacity-90">{siteLang === 'en' ? 'Studies of Defense Systems' : 'Studije odbrambenih sistema'}</span>
                            <span className="flex items-center gap-2 opacity-90">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{getReadingTime(currentArticle)} {siteLang === 'en' ? 'min read' : 'min čitanja'}</span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Navigation Arrows (hidden on mobile) */}
                <button
                    onClick={prevSlide}
                    className="hidden sm:inline-flex absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                    aria-label="Previous slide"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <button
                    onClick={nextSlide}
                    className="hidden sm:inline-flex absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                    aria-label="Next slide"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>

                {/* Mobile full-bleed clickable overlay (captures taps and swipes) */}
                <Link
                    href={getArticleUrl(currentArticle)}
                    className="absolute inset-0 sm:hidden z-10"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onClick={handleOverlayClick}
                    aria-label="Open article"
                />

                {/* Slide Indicators */}
                <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {latestArticles.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-2 rounded-full transition-all ${index === currentSlide
                                ? 'w-8 bg-white'
                                : 'w-2 bg-white/50 hover:bg-white/70'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}